/* ================================================= */
/* PASTORAL WORKFLOW ENGINE                          */
/* Pure deterministic — no AI, no DB mutations      */
/*                                                   */
/* Workflows are DERIVED from existing visit/prayer  */
/* data, not stored. They reflect the current state  */
/* of pastoral accompaniment for an entity.          */
/*                                                   */
/* Designed for extension into:                      */
/* - District assistant context                      */
/* - Smart scheduling                                */
/* - AI-generated follow-up queues                   */
/* - Predictive deterioration alerts                 */
/* ================================================= */

import {
  AlertCircle,
  HeartHandshake,
  RefreshCw,
  Heart,
  Droplets,
  HandHeart,
  Church,
} from "lucide-react";

import { TREND_TYPES, MEMORY_PATTERN_TYPES } from "./pastoral-memory";
import { VISIT_THRESHOLDS } from "./pastoral-intelligence";
import { normalizeFeedItem, FEED_PRIORITY } from "./pastoral-feed";
import { getDaysSince } from "./date";

/* ============================= */
/* WORKFLOW TYPES                 */
/* ============================= */

export const WORKFLOW_TYPES = {
  VISIT_FOLLOWUP:       "VISIT_FOLLOWUP",       // visit cadence management
  RECOVERY_PATH:        "RECOVERY_PATH",         // sustaining momentum after gap
  SPIRITUAL_SUPPORT:    "SPIRITUAL_SUPPORT",     // low spiritual score trajectory
  BAPTISM_PREPARATION:  "BAPTISM_PREPARATION",  // baptism journey tracking
  CRISIS_RESPONSE:      "CRISIS_RESPONSE",       // urgent prayer + absence = crisis
  CHURCH_RECOVERY:      "CHURCH_RECOVERY",       // church revitalization
  PRAYER_ESCALATION:    "PRAYER_ESCALATION",    // chronic prayer burden
};

/* ============================= */
/* WORKFLOW STAGES                */
/* Derived from visit momentum    */
/* ============================= */

export const WORKFLOW_STAGES = {
  DORMANT:    "dormant",    // no pastoral history yet
  INITIATED:  "initiated",  // first steps taken (≤ 2 visits in scope)
  ACTIVE:     "active",     // followup ongoing
  SUSTAINED:  "sustained",  // consistent rhythm achieved
  RECOVERING: "recovering", // momentum rebounding after gap
  STALLED:    "stalled",    // rhythm lost, needs restart
};

/* ============================= */
/* URGENCY LEVELS                 */
/* ============================= */

export const WORKFLOW_URGENCY = {
  LOW:      "low",
  MEDIUM:   "medium",
  HIGH:     "high",
  CRITICAL: "critical",
};

/* ============================= */
/* INTERNAL CONSTANTS             */
/* ============================= */

const URGENCY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

/* Stage context labels — static, no interpolation */
const STAGE_CONTEXT_LABEL = {
  [WORKFLOW_STAGES.DORMANT]:    "Sin seguimiento registrado aún",
  [WORKFLOW_STAGES.INITIATED]:  "El acompañamiento está comenzando",
  [WORKFLOW_STAGES.ACTIVE]:     "Seguimiento en proceso",
  [WORKFLOW_STAGES.SUSTAINED]:  "Seguimiento constante y saludable",
  [WORKFLOW_STAGES.RECOVERING]: "El seguimiento está retomándose",
  [WORKFLOW_STAGES.STALLED]:    "El seguimiento perdió continuidad",
};

/* Next suggested actions per workflow type + stage — static map */
const NEXT_ACTION_MAP = {
  [WORKFLOW_TYPES.VISIT_FOLLOWUP]: {
    [WORKFLOW_STAGES.DORMANT]:    "Iniciar seguimiento pastoral",
    [WORKFLOW_STAGES.INITIATED]:  "Continuar el acompañamiento iniciado",
    [WORKFLOW_STAGES.ACTIVE]:     "Mantener el ritmo de visitas",
    [WORKFLOW_STAGES.SUSTAINED]:  "Seguimiento saludable — mantener el ritmo",
    [WORKFLOW_STAGES.RECOVERING]: "Sostener la recuperación con otra visita",
    [WORKFLOW_STAGES.STALLED]:    "Retomar el seguimiento esta semana",
  },
  [WORKFLOW_TYPES.RECOVERY_PATH]: {
    [WORKFLOW_STAGES.DORMANT]:    "Iniciar proceso de recuperación",
    [WORKFLOW_STAGES.INITIATED]:  "La recuperación está comenzando",
    [WORKFLOW_STAGES.ACTIVE]:     "El ritmo se está regularizando",
    [WORKFLOW_STAGES.SUSTAINED]:  "Recuperación consolidada — mantener",
    [WORKFLOW_STAGES.RECOVERING]: "Sostener la recuperación con constancia",
    [WORKFLOW_STAGES.STALLED]:    "La recuperación se interrumpió — reiniciar",
  },
  [WORKFLOW_TYPES.SPIRITUAL_SUPPORT]: {
    [WORKFLOW_STAGES.DORMANT]:    "Iniciar acompañamiento espiritual",
    [WORKFLOW_STAGES.INITIATED]:  "Profundizar el acompañamiento espiritual",
    [WORKFLOW_STAGES.ACTIVE]:     "Continuar el acompañamiento espiritual",
    [WORKFLOW_STAGES.SUSTAINED]:  "Crecimiento espiritual en curso",
    [WORKFLOW_STAGES.RECOVERING]: "El estado espiritual está mejorando",
    [WORKFLOW_STAGES.STALLED]:    "Retomar el acompañamiento espiritual",
  },
  [WORKFLOW_TYPES.BAPTISM_PREPARATION]: {
    [WORKFLOW_STAGES.DORMANT]:    "Iniciar preparación bautismal",
    [WORKFLOW_STAGES.INITIATED]:  "Continuar preparación bautismal",
    [WORKFLOW_STAGES.ACTIVE]:     "Avanzar en el proceso de bautismo",
    [WORKFLOW_STAGES.SUSTAINED]:  "Proceso bautismal en buen ritmo",
    [WORKFLOW_STAGES.RECOVERING]: "El proceso bautismal se está retomando",
    [WORKFLOW_STAGES.STALLED]:    "Retomar el proceso de bautismo",
  },
  [WORKFLOW_TYPES.CRISIS_RESPONSE]: {
    [WORKFLOW_STAGES.DORMANT]:    "Responder a la situación crítica",
    [WORKFLOW_STAGES.INITIATED]:  "Dar seguimiento a la respuesta inicial",
    [WORKFLOW_STAGES.ACTIVE]:     "Continuar acompañamiento en la crisis",
    [WORKFLOW_STAGES.SUSTAINED]:  "La crisis está siendo acompañada",
    [WORKFLOW_STAGES.RECOVERING]: "La situación crítica está mejorando",
    [WORKFLOW_STAGES.STALLED]:    "La crisis requiere atención urgente",
  },
  [WORKFLOW_TYPES.CHURCH_RECOVERY]: {
    [WORKFLOW_STAGES.DORMANT]:    "Programar primera visita pastoral",
    [WORKFLOW_STAGES.INITIATED]:  "Continuar el proceso de recuperación",
    [WORKFLOW_STAGES.ACTIVE]:     "Mantener el acompañamiento",
    [WORKFLOW_STAGES.SUSTAINED]:  "Iglesia con seguimiento regular",
    [WORKFLOW_STAGES.RECOVERING]: "La iglesia está retomando el ritmo",
    [WORKFLOW_STAGES.STALLED]:    "Retomar el seguimiento a la iglesia",
  },
  [WORKFLOW_TYPES.PRAYER_ESCALATION]: {
    [WORKFLOW_STAGES.DORMANT]:    "Atender las oraciones activas",
    [WORKFLOW_STAGES.INITIATED]:  "Continuar intercesión y seguimiento",
    [WORKFLOW_STAGES.ACTIVE]:     "Seguir de cerca las peticiones urgentes",
    [WORKFLOW_STAGES.SUSTAINED]:  "Intercesión constante en curso",
    [WORKFLOW_STAGES.RECOVERING]: "Las peticiones están siendo atendidas",
    [WORKFLOW_STAGES.STALLED]:    "Las oraciones urgentes necesitan atención",
  },
};

/* Static feed style maps per workflow type */
const WORKFLOW_STYLE = {
  [WORKFLOW_TYPES.VISIT_FOLLOWUP]: {
    icon:               HeartHandshake,
    iconContainerClass: "bg-emerald-50 dark:bg-emerald-950/30",
    iconClass:          "text-emerald-600 dark:text-emerald-400",
  },
  [WORKFLOW_TYPES.RECOVERY_PATH]: {
    icon:               RefreshCw,
    iconContainerClass: "bg-blue-50 dark:bg-blue-950/30",
    iconClass:          "text-blue-600 dark:text-blue-400",
  },
  [WORKFLOW_TYPES.SPIRITUAL_SUPPORT]: {
    icon:               Heart,
    iconContainerClass: "bg-violet-50 dark:bg-violet-950/30",
    iconClass:          "text-violet-600 dark:text-violet-400",
  },
  [WORKFLOW_TYPES.BAPTISM_PREPARATION]: {
    icon:               Droplets,
    iconContainerClass: "bg-sky-50 dark:bg-sky-950/30",
    iconClass:          "text-sky-600 dark:text-sky-400",
  },
  [WORKFLOW_TYPES.CRISIS_RESPONSE]: {
    icon:               AlertCircle,
    iconContainerClass: "bg-red-50 dark:bg-red-950/30",
    iconClass:          "text-red-600 dark:text-red-400",
  },
  [WORKFLOW_TYPES.CHURCH_RECOVERY]: {
    icon:               Church,
    iconContainerClass: "bg-orange-50 dark:bg-orange-950/30",
    iconClass:          "text-orange-600 dark:text-orange-400",
  },
  [WORKFLOW_TYPES.PRAYER_ESCALATION]: {
    icon:               HandHeart,
    iconContainerClass: "bg-rose-50 dark:bg-rose-950/30",
    iconClass:          "text-rose-600 dark:text-rose-400",
  },
};

/* ============================= */
/* CORE: WORKFLOW HEALTH          */
/* ============================= */

/**
 * Derives continuity score (0–100) and lifecycle stage from momentum + recency.
 *
 * continuityScore semantics:
 *   80–100  "Seguimiento constante"   → urgency: low
 *   60–79   "Seguimiento en proceso"  → urgency: medium
 *   30–59   "Se está enfriando"       → urgency: high
 *    0–29   "Seguimiento detenido"    → urgency: critical
 *
 * @param {Object} momentum   — from computePastoralMomentum()
 * @param {{ daysSince, visitCount }}
 * @returns {{ continuityScore: number, stage: string }}
 */
export function evaluateWorkflowHealth(momentum, { daysSince = null, visitCount = 0 } = {}) {
  // Base score from recency
  let score = 0;
  if (daysSince === null) {
    score = 0;
  } else if (daysSince <= 14) {
    score = 95;
  } else if (daysSince <= 21) {
    score = 85;
  } else if (daysSince <= 45) {
    score = 65 - Math.round((daysSince - 21) * 0.8);
  } else if (daysSince <= 90) {
    score = 42 - Math.round((daysSince - 45) * 0.5);
  } else {
    score = Math.max(0, 20 - Math.round((daysSince - 90) * 0.15));
  }

  // Density bonus
  const visits90 = momentum.visits_90d ?? 0;
  if (visits90 >= 4)      score = Math.min(100, score + 12);
  else if (visits90 >= 2) score = Math.min(100, score + 6);

  // Trend modifiers
  if (momentum.trend === TREND_TYPES.STABLE_HEALTHY) score = Math.min(100, score + 8);
  if (momentum.trend === TREND_TYPES.IMPROVING)      score = Math.min(100, score + 5);
  if (momentum.trend === TREND_TYPES.DECLINING)      score = Math.max(0, score - 10);
  if (momentum.trend === TREND_TYPES.STABLE_NEGLECTED) score = Math.max(0, score - 20);

  const continuityScore = Math.round(Math.max(0, Math.min(100, score)));

  // Derive lifecycle stage
  let stage;
  if (daysSince === null) {
    stage = WORKFLOW_STAGES.DORMANT;
  } else if (momentum.trend === TREND_TYPES.STABLE_HEALTHY && continuityScore >= 80) {
    stage = WORKFLOW_STAGES.SUSTAINED;
  } else if (momentum.trend === TREND_TYPES.RECOVERING) {
    stage = WORKFLOW_STAGES.RECOVERING;
  } else if (momentum.trend === TREND_TYPES.STABLE_NEGLECTED || continuityScore < 25) {
    stage = WORKFLOW_STAGES.STALLED;
  } else if (visitCount <= 2 || visits90 <= 1) {
    stage = WORKFLOW_STAGES.INITIATED;
  } else {
    stage = WORKFLOW_STAGES.ACTIVE;
  }

  return { continuityScore, stage };
}

/* ============================= */
/* CORE: URGENCY                  */
/* ============================= */

/**
 * Maps continuity score + recency into an urgency level.
 * Dual-axis: urgency rises when score is low OR gap is long.
 *
 * @param {number}      continuityScore  — 0-100
 * @param {number|null} daysSinceLastAction
 * @returns {string}  WORKFLOW_URGENCY value
 */
export function computeWorkflowUrgency(continuityScore, daysSinceLastAction = null) {
  const days = daysSinceLastAction ?? 999;
  if (continuityScore < 25 || days > 90) return WORKFLOW_URGENCY.CRITICAL;
  if (continuityScore < 50 || days > 45) return WORKFLOW_URGENCY.HIGH;
  if (continuityScore < 75 || days > 21) return WORKFLOW_URGENCY.MEDIUM;
  return WORKFLOW_URGENCY.LOW;
}

/* ============================= */
/* CORE: DETECT ACTIVE WORKFLOWS  */
/* Main orchestrator              */
/* ============================= */

/**
 * Derives all currently live workflows from pastoral memory + entity state.
 * Returns workflows sorted by urgency (critical → low).
 *
 * Designed as the input contract for:
 * - WorkflowStrip UI component
 * - District assistant context
 * - Smart scheduling engine
 * - AI-generated follow-up queues (future)
 *
 * @param {Object} memory               — from buildPastoralMemory()
 * @param {{
 *   currentDaysSince?: number|null,
 *   urgentPrayers?:    number,
 *   activePrayers?:    number,
 *   spiritualScore?:   number|null,
 *   baptismStatus?:    string|null,
 *   entityType?:       "person"|"family"|"church",
 *   entityId?:         string|null,
 * }} options
 * @returns {Workflow[]}
 */
export function detectActiveWorkflows(memory, {
  currentDaysSince = null,
  urgentPrayers    = 0,
  activePrayers    = 0,
  spiritualScore   = null,
  baptismStatus    = null,
  entityType       = "person",
  entityId         = null,
} = {}) {
  if (!memory || !memory.momentum) return [];

  const { momentum, patterns } = memory;
  const patternSet = new Set((patterns || []).map((p) => p.type));
  const workflows  = [];
  const seen       = new Set();

  const add = (wf) => {
    if (!seen.has(wf.type)) {
      seen.add(wf.type);
      workflows.push(wf);
    }
  };

  const visitCount = (momentum.visits_90d ?? 0) + (momentum.visits_30d ?? 0);

  // ── CHURCH_RECOVERY (church entity only) ──────────────────────────────
  if (entityType === "church") {
    if (currentDaysSince === null || currentDaysSince > VISIT_THRESHOLDS.neglected) {
      const { continuityScore, stage } = evaluateWorkflowHealth(momentum, {
        daysSince:  currentDaysSince,
        visitCount,
      });
      const urgency = stage === WORKFLOW_STAGES.DORMANT
        ? WORKFLOW_URGENCY.MEDIUM
        : computeWorkflowUrgency(continuityScore, currentDaysSince);

      add({
        type:               WORKFLOW_TYPES.CHURCH_RECOVERY,
        entityType,
        entityId,
        stage,
        momentum:           momentum.trend,
        urgency,
        continuityScore,
        lastActionAt:       null,
        daysSinceLastAction: currentDaysSince,
        nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.CHURCH_RECOVERY][stage],
        contextLabel:        STAGE_CONTEXT_LABEL[stage],
      });
    }

    // Churches also get prayer escalation when prayer burden is high
    if (urgentPrayers > 0 && activePrayers >= 2) {
      add({
        type:               WORKFLOW_TYPES.PRAYER_ESCALATION,
        entityType,
        entityId,
        stage:              WORKFLOW_STAGES.ACTIVE,
        momentum:           momentum.trend,
        urgency:            WORKFLOW_URGENCY.HIGH,
        continuityScore:    50,
        lastActionAt:       null,
        daysSinceLastAction: currentDaysSince,
        nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.PRAYER_ESCALATION][WORKFLOW_STAGES.ACTIVE],
        contextLabel:        "Múltiples peticiones activas — se recomienda intercesión sostenida",
      });
    }

    workflows.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4));
    return workflows;
  }

  // ── PERSON / FAMILY workflows ─────────────────────────────────────────

  // CRISIS_RESPONSE — urgent prayers + absence = pastoral crisis
  if (urgentPrayers > 0 && (currentDaysSince === null || currentDaysSince > VISIT_THRESHOLDS.attention)) {
    const { continuityScore, stage } = evaluateWorkflowHealth(momentum, { daysSince: currentDaysSince, visitCount });
    const resolvedStage = stage === WORKFLOW_STAGES.SUSTAINED ? WORKFLOW_STAGES.ACTIVE : stage;
    add({
      type:               WORKFLOW_TYPES.CRISIS_RESPONSE,
      entityType,
      entityId,
      stage:              resolvedStage,
      momentum:           momentum.trend,
      urgency:            WORKFLOW_URGENCY.CRITICAL,
      continuityScore,
      lastActionAt:       null,
      daysSinceLastAction: currentDaysSince,
      nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.CRISIS_RESPONSE][resolvedStage] ?? "Atender situación urgente",
      contextLabel:        "Hay oraciones urgentes que requieren acompañamiento pastoral",
    });
  }

  // VISIT_FOLLOWUP — triggered when overdue or never visited
  if (currentDaysSince === null || currentDaysSince > VISIT_THRESHOLDS.attention) {
    const { continuityScore, stage } = evaluateWorkflowHealth(momentum, { daysSince: currentDaysSince, visitCount });
    const urgency = stage === WORKFLOW_STAGES.DORMANT
      ? WORKFLOW_URGENCY.MEDIUM
      : computeWorkflowUrgency(continuityScore, currentDaysSince);

    add({
      type:               WORKFLOW_TYPES.VISIT_FOLLOWUP,
      entityType,
      entityId,
      stage,
      momentum:           momentum.trend,
      urgency,
      continuityScore,
      lastActionAt:       null,
      daysSinceLastAction: currentDaysSince,
      nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.VISIT_FOLLOWUP][stage],
      contextLabel:        STAGE_CONTEXT_LABEL[stage],
    });
  }

  // RECOVERY_PATH — explicitly tracking momentum rebound
  if (momentum.trend === TREND_TYPES.RECOVERING) {
    const { continuityScore } = evaluateWorkflowHealth(momentum, { daysSince: currentDaysSince, visitCount });
    const rawUrgency = computeWorkflowUrgency(continuityScore, currentDaysSince);
    add({
      type:               WORKFLOW_TYPES.RECOVERY_PATH,
      entityType,
      entityId,
      stage:              WORKFLOW_STAGES.RECOVERING,
      momentum:           momentum.trend,
      // Recovery always at least medium — encourage continuation
      urgency:            rawUrgency === WORKFLOW_URGENCY.LOW ? WORKFLOW_URGENCY.MEDIUM : rawUrgency,
      continuityScore,
      lastActionAt:       null,
      daysSinceLastAction: currentDaysSince,
      nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.RECOVERY_PATH][WORKFLOW_STAGES.RECOVERING],
      contextLabel:        "El seguimiento está retomándose — es importante sostener el ritmo",
    });
  }

  // PRAYER_ESCALATION — chronic prayer burden
  if (patternSet.has(MEMORY_PATTERN_TYPES.CHRONIC_PRAYER_PATTERN)) {
    add({
      type:               WORKFLOW_TYPES.PRAYER_ESCALATION,
      entityType,
      entityId,
      stage:              WORKFLOW_STAGES.ACTIVE,
      momentum:           momentum.trend,
      urgency:            WORKFLOW_URGENCY.HIGH,
      continuityScore:    50,
      lastActionAt:       null,
      daysSinceLastAction: currentDaysSince,
      nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.PRAYER_ESCALATION][WORKFLOW_STAGES.ACTIVE],
      contextLabel:        "Múltiples peticiones activas — se recomienda intercesión sostenida",
    });
  }

  // SPIRITUAL_SUPPORT — low spiritual score
  if (patternSet.has(MEMORY_PATTERN_TYPES.LOW_SPIRITUAL_CONCERN)) {
    const { continuityScore, stage } = evaluateWorkflowHealth(momentum, { daysSince: currentDaysSince, visitCount });
    const resolvedStage = stage === WORKFLOW_STAGES.SUSTAINED ? WORKFLOW_STAGES.ACTIVE : stage;
    const urgency = spiritualScore !== null && spiritualScore <= 1
      ? WORKFLOW_URGENCY.CRITICAL
      : WORKFLOW_URGENCY.HIGH;

    add({
      type:               WORKFLOW_TYPES.SPIRITUAL_SUPPORT,
      entityType,
      entityId,
      stage:              resolvedStage,
      momentum:           momentum.trend,
      urgency,
      continuityScore,
      lastActionAt:       null,
      daysSinceLastAction: currentDaysSince,
      nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.SPIRITUAL_SUPPORT][resolvedStage] ?? "Acompañamiento espiritual recomendado",
      contextLabel:        "El estado espiritual requiere acompañamiento pastoral",
    });
  }

  // BAPTISM_PREPARATION — baptism journey
  if (baptismStatus === "estudiando") {
    const { continuityScore, stage } = evaluateWorkflowHealth(momentum, { daysSince: currentDaysSince, visitCount });
    const resolvedStage = stage === WORKFLOW_STAGES.DORMANT ? WORKFLOW_STAGES.INITIATED : stage;
    add({
      type:               WORKFLOW_TYPES.BAPTISM_PREPARATION,
      entityType,
      entityId,
      stage:              resolvedStage,
      momentum:           momentum.trend,
      urgency:            WORKFLOW_URGENCY.MEDIUM,
      continuityScore,
      lastActionAt:       null,
      daysSinceLastAction: currentDaysSince,
      nextSuggestedAction: NEXT_ACTION_MAP[WORKFLOW_TYPES.BAPTISM_PREPARATION][resolvedStage],
      contextLabel:        "Candidato al bautismo — requiere preparación sostenida",
    });
  }

  workflows.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 4) - (URGENCY_ORDER[b.urgency] ?? 4));
  return workflows;
}

/* ============================= */
/* ADVANCE WORKFLOW               */
/* Pure projection utility        */
/* ============================= */

/**
 * Projects what a workflow would look like after a new pastoral action.
 * Does NOT mutate state — returns a new workflow object.
 *
 * Useful for:
 * - "What happens if I visit today?" projections
 * - Future AI assistant "suggested next step" previews
 * - Smart scheduling engine inputs
 *
 * @param {Workflow} workflow
 * @param {{ newActionDate: string }} options — ISO date of the new action
 * @returns {Workflow}
 */
export function advanceWorkflow(workflow, { newActionDate = null } = {}) {
  if (!newActionDate) return workflow;

  const newDaysSince = getDaysSince(newActionDate);

  // Projected momentum: assume improving trajectory after new action
  const projectedMomentum = {
    trend:      newDaysSince <= 21 ? TREND_TYPES.IMPROVING : TREND_TYPES.UNKNOWN,
    visits_90d: (workflow.meta?.visits_90d ?? 0) + 1,
    visits_30d: (workflow.meta?.visits_30d ?? 0) + 1,
    avg_cadence: null,
    confidence:  0.7,
  };

  const { continuityScore, stage } = evaluateWorkflowHealth(projectedMomentum, {
    daysSince:  newDaysSince,
    visitCount: 3,
  });
  const urgency = stage === WORKFLOW_STAGES.DORMANT
    ? WORKFLOW_URGENCY.MEDIUM
    : computeWorkflowUrgency(continuityScore, newDaysSince);

  return {
    ...workflow,
    stage,
    continuityScore,
    daysSinceLastAction: newDaysSince,
    urgency,
    nextSuggestedAction: NEXT_ACTION_MAP[workflow.type]?.[stage] ?? workflow.nextSuggestedAction,
    contextLabel:        STAGE_CONTEXT_LABEL[stage] ?? workflow.contextLabel,
  };
}

/* ============================= */
/* BUILD WORKFLOW FEED            */
/* Converts workflows → FeedItems */
/* Compatible with mergeOperational */
/* ============================= */

const ACTIVE_FEED_STAGES = new Set([
  WORKFLOW_STAGES.INITIATED,
  WORKFLOW_STAGES.ACTIVE,
  WORKFLOW_STAGES.RECOVERING,
  WORKFLOW_STAGES.STALLED,
]);

/**
 * Converts active workflows into normalized FeedItem[].
 * DORMANT and SUSTAINED stages are excluded — no noise in feed.
 *
 * @param {Workflow[]} workflows
 * @param {{ nav?, entityRoute? }}
 * @returns {FeedItem[]}
 */
export function buildWorkflowFeed(workflows, { nav, entityRoute } = {}) {
  return workflows
    .filter((w) => ACTIVE_FEED_STAGES.has(w.stage))
    .map((w) => {
      const style = WORKFLOW_STYLE[w.type] ?? WORKFLOW_STYLE[WORKFLOW_TYPES.VISIT_FOLLOWUP];
      const feedPriority = w.urgency === WORKFLOW_URGENCY.CRITICAL ? FEED_PRIORITY.critical
                         : w.urgency === WORKFLOW_URGENCY.HIGH     ? FEED_PRIORITY.warning
                         : FEED_PRIORITY.normal;

      return normalizeFeedItem({
        id:                 `workflow-${w.type}-${w.entityId ?? "entity"}`,
        type:               "workflow",
        ...style,
        title:              w.contextLabel,
        subtitle:           w.nextSuggestedAction,
        priority:           feedPriority,
        _date:              null, // undated → sorts to top within priority bucket
        onClick:            nav && entityRoute ? () => nav(entityRoute) : undefined,
        entityType:         w.entityType,
        entityId:           w.entityId,
        meta:               {
          workflowType:    w.type,
          stage:           w.stage,
          urgency:         w.urgency,
          continuityScore: w.continuityScore,
        },
      });
    });
}
